/**
 * Session 3D — shared shell + primitives for the 31 /me pages.
 *
 * Per §3.1 every /me page has:
 *   - PageHeader (warm Khidmat tone — "Your leave balance" not
 *     "Leave Balances")
 *   - actions row (+ Request New X · Ask Khidmat ✦)
 *   - 1-2 InlineAISuggestion cards (Khidmat-led personal nudges)
 *   - DataTable or detail card + AiBriefPanel (Khidmat narrating)
 *
 * Per §0.1 Khidmat is the ONLY narrating agent on /me. Other agent
 * names are deliberately NOT exported from this module.
 *
 * Per §0.7 + Commandments 73 + 74: no stubs, no hardcoded EN.
 *
 * Exports:
 *   <Session3dMeShell />     — the locked grid 1fr + 280px layout
 *   <MeActionsRow />          — request + ask-Khidmat actions
 *   <AskKhidmatButton />      — opens chat panel with prefilled question
 *   <RequestChangeViaKhidmat /> — "Request change via Khidmat" affordance
 *   useCurrentEmployeeId      — re-exported from session3d-api for ergonomics
 */

import { MessageCircle, Plus, Sparkles } from "lucide-react";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { AiBriefPanel } from "../shell/AiBriefPanel";
import { PageHeader } from "../components/ui";
import { apiFetch } from "../lib/api";
import { openChatPanel } from "../lib/chat-explain";
import {
  readCurrentEmployeeId,
  writeCurrentEmployeeId,
} from "../lib/session3d-api";

// ---------------------------------------------------------------------------
// useCurrentEmployeeId hook (mirrors useApproverEmployeeId)
// ---------------------------------------------------------------------------

// Per-process flag so we only hit /api/me once across mounts (every
// /me page used to call this hook on mount; without the guard, 30
// page navigations = 30 round-trips for the same id).
let _meResolveInflight: Promise<string | null> | null = null;
let _meResolveDone = false;

async function _resolveMeOnce(): Promise<string | null> {
  if (_meResolveDone) return readCurrentEmployeeId();
  if (_meResolveInflight) return _meResolveInflight;
  _meResolveInflight = (async () => {
    try {
      const res = await apiFetch("/api/me");
      if (!res.ok) return null;
      const data = (await res.json()) as { employee_id?: string };
      if (data?.employee_id) {
        writeCurrentEmployeeId(data.employee_id);
        return data.employee_id;
      }
      return null;
    } catch {
      return null;
    } finally {
      _meResolveDone = true;
      _meResolveInflight = null;
    }
  })();
  return _meResolveInflight;
}

export function useCurrentEmployeeId(): string {
  const [id, setId] = useState<string>("");
  useEffect(() => {
    const local = readCurrentEmployeeId() ?? "";
    if (local) {
      setId(local);
      return;
    }
    // No URL param + no cached id → ask /api/me. M3X.AUTH wires the
    // Clerk session → Employee row binding; on first sign-in the
    // backend back-stamps `employees.clerk_user_id` by JWT email.
    let cancelled = false;
    _resolveMeOnce().then((resolved) => {
      if (!cancelled && resolved) setId(resolved);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  return id;
}

/**
 * Like `useCurrentEmployeeId` but also reports whether the /api/me
 * lookup is still in flight. The gate uses this so the manual-paste
 * picker doesn't flash while we're waiting for the Clerk-bound id.
 */
export function useCurrentEmployeeIdState(): {
  id: string;
  isResolving: boolean;
} {
  const [id, setId] = useState<string>("");
  const [isResolving, setIsResolving] = useState<boolean>(true);
  useEffect(() => {
    const local = readCurrentEmployeeId() ?? "";
    if (local) {
      setId(local);
      setIsResolving(false);
      return;
    }
    let cancelled = false;
    _resolveMeOnce()
      .then((resolved) => {
        if (cancelled) return;
        if (resolved) setId(resolved);
      })
      .finally(() => {
        if (!cancelled) setIsResolving(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);
  return { id, isResolving };
}

export function setCurrentEmployeeId(employeeId: string | null): void {
  writeCurrentEmployeeId(employeeId);
}

// ---------------------------------------------------------------------------
// Session3dMeShell — the locked layout
// ---------------------------------------------------------------------------

export interface Session3dMeShellProps {
  title: string;
  subtitle: string;
  /** Khidmat brief context. */
  briefContext: {
    total: number;
    attention: number;
    critical: number;
    top_kind: string;
    window_label: string;
  };
  actionsRow?: ReactNode;
  children: ReactNode;
}

export function Session3dMeShell(props: Session3dMeShellProps) {
  const { title, subtitle, briefContext, actionsRow, children } = props;
  return (
    <div className="grid grid-cols-[1fr_280px] gap-4">
      <div className="space-y-3">
        <PageHeader title={title} subtitle={subtitle} />
        {actionsRow}
        {children}
      </div>
      <aside className="rounded-2xl border border-border-subtle bg-white">
        <AiBriefPanel agent="khidmat" liveContext={briefContext} />
      </aside>
    </div>
  );
}

// ---------------------------------------------------------------------------
// MeActionsRow — [+ Request New X] [Ask Khidmat ✦]
// ---------------------------------------------------------------------------

export interface MeActionsRowProps {
  /** Optional left-aligned hint text. */
  hint?: string;
  /** Primary 'Request New ...' CTA. Omit for read-only screens. */
  requestLabel?: string;
  onRequestClick?: () => void;
  /** Pre-filled question for the Khidmat chat. */
  khidmatPrefill: string;
}

export function MeActionsRow(props: MeActionsRowProps) {
  const { hint, requestLabel, onRequestClick, khidmatPrefill } = props;
  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div className="text-xs text-text-tertiary">{hint ?? ""}</div>
      <div className="flex items-center gap-1.5">
        <AskKhidmatButton prefill={khidmatPrefill} />
        {requestLabel && onRequestClick && (
          <button
            type="button"
            onClick={onRequestClick}
            className="inline-flex items-center gap-1.5 rounded-md bg-gradient-to-br from-[#2260C9] to-[#A846BF] px-3 py-1.5 text-sm font-medium text-white hover:opacity-95"
          >
            <Plus className="h-4 w-4" />
            {requestLabel}
          </button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AskKhidmatButton — opens chat panel with pre-filled question
// ---------------------------------------------------------------------------

export interface AskKhidmatButtonProps {
  prefill: string;
  /** Visual variant. */
  variant?: "primary" | "ghost";
}

export function AskKhidmatButton(props: AskKhidmatButtonProps) {
  const { t } = useTranslation();
  const { prefill, variant = "ghost" } = props;
  const handleClick = () => openChatPanel({ prefillInput: prefill });
  const className =
    variant === "primary"
      ? "inline-flex items-center gap-1.5 rounded-md bg-gradient-to-br from-[#00D3C2] to-[#2260C9] px-3 py-1.5 text-sm font-medium text-white hover:opacity-95"
      : "inline-flex items-center gap-1 rounded border border-[#e9d5ff] bg-[#f5f3ff] px-2.5 py-1.5 text-xs font-medium text-[#6b21a8] hover:bg-[#ede9fe]";
  return (
    <button type="button" onClick={handleClick} className={className}>
      <span aria-hidden="true">✦</span>
      <MessageCircle className="h-3 w-3" />
      {t("me.ask_khidmat", "Ask Khidmat")}
    </button>
  );
}

// ---------------------------------------------------------------------------
// RequestChangeViaKhidmat — read-only-field affordance per §3.3
// ---------------------------------------------------------------------------

export interface RequestChangeViaKhidmatProps {
  fieldLabel: string;
  topicHint: string;
}

export function RequestChangeViaKhidmat(props: RequestChangeViaKhidmatProps) {
  const { t } = useTranslation();
  const { fieldLabel, topicHint } = props;
  return (
    <button
      type="button"
      onClick={() =>
        openChatPanel({
          prefillInput: t(
            "me.request_change_via_khidmat_prefill",
            `I need to update my ${fieldLabel.toLowerCase()}. ${topicHint}`,
          ),
        })
      }
      className="inline-flex items-center gap-1 rounded border border-[#e9d5ff] bg-[#f5f3ff] px-2 py-0.5 text-[10px] font-medium text-[#6b21a8] hover:bg-[#ede9fe]"
    >
      <Sparkles className="h-3 w-3" />
      {t("me.request_change", "Request change via Khidmat")}
    </button>
  );
}

// ---------------------------------------------------------------------------
// MeEmptyState — warm, employee-friendly empty
// ---------------------------------------------------------------------------

export interface MeEmptyStateProps {
  headline: string;
  body: string;
  khidmatPrefill?: string;
}

export function MeEmptyState(props: MeEmptyStateProps) {
  const { headline, body, khidmatPrefill } = props;
  return (
    <div className="rounded-2xl border border-dashed border-border-subtle bg-surface-sunken/40 p-6 text-center text-sm">
      <div className="font-medium text-text-primary">
        <span aria-hidden="true">✦ </span>
        {headline}
      </div>
      <div className="mt-1 text-xs text-text-secondary">{body}</div>
      {khidmatPrefill && (
        <div className="mt-3 flex justify-center">
          <AskKhidmatButton prefill={khidmatPrefill} />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// MeEmployeeRequiredGate — show a picker when no employee id set
// ---------------------------------------------------------------------------

export function MeEmployeeRequiredGate(props: { children: ReactNode }) {
  const { t } = useTranslation();
  const { id, isResolving } = useCurrentEmployeeIdState();
  const [input, setInput] = useState("");

  if (id) return <>{props.children}</>;

  // M3X.AUTH — wait for the /api/me lookup to finish so the manual-
  // paste picker doesn't flash for users who ARE linked to an
  // Employee row but haven't visited yet.
  if (isResolving) {
    return (
      <div className="mx-auto mt-12 max-w-md rounded-2xl border border-border-subtle bg-white p-6 text-center text-sm text-text-tertiary">
        {t("me.pick_employee.resolving", "Looking up your employee profile…")}
      </div>
    );
  }

  return (
    <div className="mx-auto mt-12 max-w-md rounded-2xl border border-border-subtle bg-white p-6 text-sm">
      <h2 className="text-base font-semibold text-text-primary">
        {t("me.pick_employee.title", "Pick the employee you're acting as")}
      </h2>
      <p className="mt-1 text-xs text-text-secondary">
        {t(
          "me.pick_employee.hint",
          "Your Clerk email isn't linked to an Employee record. Ask HR to set your work_email on the employee profile, or paste a UUID below for testing.",
        )}
      </p>
      <input
        type="text"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        placeholder="00000000-0000-0000-0000-000000000000"
        className="mt-3 w-full rounded-lg border border-border-subtle px-3 py-2 font-mono text-xs"
      />
      <button
        type="button"
        onClick={() => {
          if (input.trim()) {
            writeCurrentEmployeeId(input.trim());
            if (typeof window !== "undefined") window.location.reload();
          }
        }}
        className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-gradient-to-br from-[#2260C9] to-[#A846BF] px-3 py-1.5 text-sm font-medium text-white hover:opacity-95"
      >
        {t("me.pick_employee.continue", "Continue as this employee")}
      </button>
    </div>
  );
}
